import { UserService } from "@/classes/services/UserService";
import Pagination from "@/classes/Pagination";
import User from "@/classes/User";
import DateParse from "@/classes/helpers/DateParse";
import { AlertType } from "@/classes/enums/AlertType";
import ErrorMessageHelper from "@/classes/helpers/ErrorMessageHelper";
import { AxiosError } from "axios";
import Status from "@/classes/Status";
import { Role } from "@/classes/Role";
import { get } from "http";

// pagination count differs by environment due to the Auth0 dev tenant's lower global limit of requests per second
const userPagination = new Pagination(1, 25);

const userService = new UserService();

const state = {
  searchedUserItems: [],
  existingUserItems: [],
  pagination: userPagination,
  isLoading: false,
  alertType: "",
  alertText: "",
  statuses: Array<Status>(),
  passwordResetMessage: "",
  passwordChangedSuccessfully: false,
  roles: Array<Role>(),
  emailSaved: false,
  rolesSaved: false,
  query: "",
  searchedByRoleUserItems: [],
  totalUserCount: 0,
  allUsers: [],
  allUserCount: 0,
  allUsersPagination: new Pagination(1, 25),
  isAllUsersLoading: false,
};

const getters = {
  getSearchedUserItems: () => {
    return state.searchedUserItems;
  },

  getsearchedByRoleUserItems: () => {
    return state.searchedByRoleUserItems;
  },

  getExistingUserItems: () => {
    return state.existingUserItems;
  },

  getIsLoading: () => {
    return state.isLoading;
  },

  getAlertTypes: () => {
    return state.alertType;
  },

  getAlertText: () => {
    return state.alertText;
  },

  getPagination: () => {
    return state.pagination;
  },

  getStatuses: () => {
    return state.statuses;
  },
  getPasswordChangedSuccessfully: () => {
    return state.passwordChangedSuccessfully;
  },
  getRoles: () => {
    return state.roles;
  },
  getRolesSaved: () => {
    return state.rolesSaved;
  },
  getEmailSaved: () => {
    return state.emailSaved;
  },
  getQuery: () => {
    return state.query;
  },
  getTotalUserCount: () => {
    return state.totalUserCount;
  },
  getAllUsers: () => {
    return state.allUsers;
  },
  getAllUsersPagination: () => {
    return state.allUsersPagination;
  },
  getIsAllUsersLoading: () => {
    return state.isAllUsersLoading;
  },
  getAllUserCount: () => {
    return state.allUserCount;
  }
  
};

const mutations = {
  setSearchedUserItems(state, payload) {
    state.searchedUserItems = payload;
  },

  setsearchedByRoleUserItems(state, payload) {
    state.searchedByRoleUserItems = payload;
  },

  setExistingUserItems(state, payload) {
    state.existingUserItems = payload;
  },

  setIsLoading(state, payload) {
    state.isLoading = payload;
  },

  setAlertType(state, payload) {
    state.alertType = payload;
  },

  setAlertText(state, payload) {
    state.alertText = payload;
  },

  setPagination(state, payload) {
    state.pagination = payload;
  },

  setStatuses(state, payload) {
    state.statuses = payload;
  },
  setPasswordResetMessage(state, payload) {
    state.passwordResetMessage = payload;
  },
  setPasswordChangedSuccessfully(state, payload) {
    state.passwordChangedSuccessfully = payload;
  },
  setRoles(state, payload) {
    state.roles = payload;
  },
  setRolesSaved(state, payload) {
    state.rolesSaved = payload;
  },
  setEmailSaved(state, payload) {
    state.emailSaved = payload;
  },
  setQuery(state, payload) {
    state.query = payload;
  },
  setTotalUserCount(state, payload) {
    state.totalUserCount = payload;
  },
  setAllUsers(state, payload) {
    state.allUsers = payload;
  },

  setAllUsersPagination(state, payload) {
    state.allUsersPagination = payload;
  },

  setIsAllUsersLoading(state, payload) {
    state.isAllUsersLoading = payload;
  },
  setAllUsersCount(state, payload) {
    state.allUserCount = payload;
  }
};

const actions = {
  async searchAsync({ state, commit, dispatch }, payload) {
    dispatch("clearAlertAsync");
    commit("setIsLoading", true);
    commit("setExistingUserItems", []);
    if (payload.orgId != null) {
      userService
        .searchUsers({
          params: {
            orgId: payload.orgId,
            page: state.pagination.page,
            pageSize: state.pagination.itemsPerPage,
            status: state.statusToFilterBy,
            sortBy: state.pagination.sortBy[0],
            sortDesc: state.pagination.sortDesc[0],
            query: state.query,
          },
        })
        .then((response: any) => {
          const users = response.data.items.map((datum: any) => {
            const user = new User(
              datum.id,
              datum.email,
              datum.displayName,
              datum.firstName,
              datum.lastName,
              datum.organization.name,
              datum.description,
              DateParse.ParseTimeFromUTC(datum.lastLoggedInAt),
              DateParse.ParseTimeFromUTC(datum.createdAt),
              DateParse.ParseTimeFromUTC(datum.updatedAt)
            );
            user.isSSOUser = datum.isSSOUser;
            user.roleNames = datum.roles;
            user.status = state.statuses.find(
              (s: any) => s.intValue === datum.status
            );
            user.roleNames = datum.roles;
            user.isActive = datum.status === 100;
            return user;
          });
          if (payload.role) {
            commit(
              "setSearchedUserItems",
              users.filter((u) => u.roleNames.includes(payload.role))
            );
          } else {
            commit("setSearchedUserItems", users);
            commit("setTotalUserCount", response.data.totalItems);
          }
          commit("setIsLoading", false);
        })
        .catch((error: AxiosError<Error>) => {
          commit("setIsLoading", false);
          dispatch("clearAlertAsync");
          dispatch("setErrorAlertAsync", error);
        });
    }
  },

  async searchByRoleAsync({ state, commit, dispatch }, payload) {
    dispatch("clearAlertAsync");
    commit("setIsLoading", true);
    commit("setExistingUserItems", []);
    userService
      .searchUsersByRoles({
        params: {
          orgId: payload.orgId,
          role: payload.role,
        },
      })
      .then((response: any) => {
        const users = response.data.items.map((datum: any) => {
          const user = new User(
            datum.id,
            datum.email,
            datum.displayName,
            datum.firstName,
            datum.lastName,
            datum.organization.name,
            datum.description,
            DateParse.ParseTimeFromUTC(datum.lastLoggedInAt),
            DateParse.ParseTimeFromUTC(datum.createdAt),
            DateParse.ParseTimeFromUTC(datum.updatedAt)
          );
          user.isSSOUser = datum.isSSOUser;
          user.status = state.statuses.find(
            (s: any) => s.intValue === datum.status
          );
          user.roleNames = datum.roles;
          return user;
        });
        commit("setSearchedUserItems", users);
        commit("setTotalUserCount", response.data.totalItems);
        commit("setIsLoading", false);
      })
      .catch((error: AxiosError<Error>) => {
        commit("setIsLoading", false);
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  // 7/7/20 - upon planned addition of org roles in app metadata in Auth0, this call can be deprecated
  // and user org role assignment can be handled in the .then() block of the searchAsync function.
  async getSearchedUserRolesAsync({ state, commit, rootState, dispatch }) {
    for (const user of state.searchedUserItems) {
      userService
        .getUserRoles(user.id)
        .then((response) => {
          user.roleNames = response.data.map((i) => i.name).join(", ");
          for (const datum of response.data) {
            if (datum.id === rootState.organizations.orgAdminRole) {
              state.existingUserItems.push(user);
            }
          }
          // if (idx === state.searchedUserItems.length - 1) {
          commit("setIsLoading", false);
          // }
        })
        .catch((error: AxiosError<Error>) => {
          commit("setIsLoading", false);
          dispatch("clearAlertAsync");
          dispatch("setErrorAlertAsync", error);
        });
    }
  },

  async assignRoleAsync({ rootState, dispatch }, payload) {
    dispatch("clearAlertAsync");
    userService
      .assignOrganizationAdmin(payload.id, {
        roleIds: [rootState.organizations.orgAdminRole],
      })
      .then(() => {
        dispatch(
          "setSuccessAlertAsync",
          `${payload.displayName} has been assigned Organization Admin.`
        );
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async removeRoleAsync({ rootState, dispatch }, payload) {
    dispatch("clearAlertAsync");
    userService
      .removeOrganizationAdmin(payload.id, {
        roleIds: [rootState.organizations.orgAdminRole],
      })
      .then(() => {
        dispatch(
          "setSuccessAlertAsync",
          `${payload.displayName} has been removed as Organization Admin.`
        );
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async exportUsersandGroups({ rootState }, payload) {
    await userService.exportUsersandGroups(payload);
  },

  async setErrorAlertAsync({ commit }, error?: AxiosError<Error>) {
    commit("setAlertType", AlertType.ERROR);
    commit("setAlertText", ErrorMessageHelper.createErrorMessage(error));
  },

  async setSuccessAlertAsync({ commit }, message) {
    commit("setAlertType", AlertType.SUCCESS);
    commit("setAlertText", message);
  },

  async clearAlertAsync({ commit }) {
    commit("setAlertType", AlertType.EMPTY);
    commit("setAlertText", "");
  },

  async getStatusesAsync({ commit }) {
    userService.getStatuses(true).then((response) => {
      const statuses = response.data.map((datum: any) => {
        return new Status(datum.displayName, datum.intValue);
      });
      commit("setStatuses", statuses);
    });
  },

  async updateStatus({ commit, dispatch }, payload) {
    userService
      .updateStatus(payload.id, { status: payload.status })
      .then(() => {
        dispatch("searchAsync", {
          orgId: payload.orgId,
          query: state.query,
        });
        dispatch("setSuccessAlertAsync", `Save successfull.`);
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async updateEmail({ commit, dispatch }, payload) {
    userService
      .changeEmail(payload.id, { email: payload.email })
      .then(() => {
        dispatch("searchAsync", {
          orgId: payload.orgId,
          query: state.query,
        });
        commit("setEmailSaved", true);
        dispatch("setSuccessAlertAsync", `Email change successfull`);
      })
      .catch((error: AxiosError<Error>) => {
        commit("setEmailSaved", true);
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async changePassword({ commit, dispatch }, payload) {
    userService
      .changePassword(payload.id, { password: payload.password })
      .then(() => {
        state.passwordResetMessage = "";
        dispatch("searchAsync", {
          orgId: payload.orgId,
          query: state.query,
        });
        commit("setPasswordChangedSuccessfully", true);
        dispatch("setSuccessAlertAsync", `Password change successfull.`);
      })
      .catch((error: AxiosError<Error>) => {
        state.passwordResetMessage = ErrorMessageHelper.createErrorMessage(
          error
        );
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async getRoles({ commit }) {
    userService.getRoles().then((response) => {
      const roles = response.data.map((datum: any) => {
        return new Role(datum.id, datum.name, datum.description);
      });
      commit("setRoles", roles);
    });
  },

  async assignRoles({ commit, dispatch }, payload) {
    userService
      .assignRoles(payload.id, { roleIds: payload.roleIds })
      .then(() => {
        dispatch("searchAsync", {
          orgId: payload.orgId,
          query: state.query,
        });
        commit("setRolesSaved", true);
        dispatch("setSuccessAlertAsync", `Save successful.`);
      })
      .catch((error: AxiosError<Error>) => {
        commit("setRolesSaved", true);
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async signUpUser({ commit, dispatch }, payload) {
    userService
      .signUpUser(payload.user.id, payload.signUp)
      .then((response) => {
        dispatch("searchAsync", {
          orgId: payload.orgId,
          query: state.query,
        });
        const div = document.createElement("div");
        div.innerHTML = response.data;
        const text = div.textContent || div.innerText || "";
        dispatch("setSuccessAlertAsync", text);
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async changeUserOrg({ commit, dispatch }, payload) {
    userService
      .changeUserOrg(payload)
      .then((response) => {
        dispatch("searchAsync", {
          orgId: payload.originalOrgId,
          query: state.query,
        });
        var result: Array<string> = response.data;
        if (result.length > 0) {
          dispatch("setSuccessAlertAsync", result[0]);
        } else {
          dispatch(
            "setSuccessAlertAsync",
            "Organization of the user change successfull."
          );
        }
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async getAllUsers({ state, commit, dispatch }, payload) {
      commit("setIsAllUsersLoading", true);
      const pagination = {
        ...state.allUsersPagination,
      };

      // Ensure statuses are loaded so we can map numeric status values
      // to Status objects when creating User instances.
      if (!state.statuses || state.statuses.length === 0) {
        try {
          const resp: any = await userService.getStatuses(true);
          const statuses = resp.data.map((datum: any) => {
            return new Status(datum.displayName, datum.intValue);
          });
          commit("setStatuses", statuses);
        } catch (e) {
          // if statuses can't be loaded, proceed without them (users.status will be undefined)
          // optionally, log or surface an alert in the future
        }
      }

      const params: any = {
        sortBy:
          payload && payload.sortBy != null
            ? payload.sortBy
            : state.allUsersPagination.sortBy[0],
        sortDesc:
          payload && payload.sortDesc != null
            ? payload.sortDesc
            : state.allUsersPagination.sortDesc[0],
        page:
          payload && payload.page != null
            ? payload.page
            : state.allUsersPagination.page,
        pageSize:
          payload && payload.pageSize != null
            ? payload.pageSize
            : state.allUsersPagination.itemsPerPage,
      };

      if (payload) {
        if (payload.query && payload.query.toString().trim().length > 0) {
          params.query = payload.query;
        }
        if (payload.queryType != null && payload.queryType !== "") {
          params.queryType = payload.queryType;
        }
        if (payload.status != null && payload.status !== "") {
          params.status = payload.status;
        }

        if (payload.sortBy != null) {
          pagination.sortBy = [payload.sortBy];
        }
        if (payload.sortDesc != null) {
          pagination.sortDesc = [payload.sortDesc];
        }
        if (payload.page != null) {
          pagination.page = payload.page;
        }
        if (payload.pageSize != null) {
          pagination.itemsPerPage = payload.pageSize;
        }
      }
      commit("setAllUsersPagination", pagination);
      userService
        .getallUsers({
          params,
        })
        .then((response: any) => {
          const payloadItems =
            response.data && response.data.items
              ? response.data.items
              : response.data;
          const users = (Array.isArray(payloadItems) ? payloadItems : []).map(
            (datum: any) => {
              const user = new User(
                datum.id,
                datum.email,
                datum.displayName,
                datum.firstName,
                datum.lastName,
                datum.organization.name,
                datum.description,
                DateParse.ParseTimeFromUTC(datum.lastLoggedInAt),
                DateParse.ParseTimeFromUTC(datum.createdAt),
                DateParse.ParseTimeFromUTC(datum.updatedAt)
              );
            user.isSSOUser = datum.isSSOUser;
            user.roleNames = datum.roles;
            user.status = state.statuses.find(
              (s: any) => s.intValue === datum.status
            );
            user.roleNames = datum.roles;
            user.isActive = datum.status === 100;
            return user;
          });
            commit("setAllUsers", users);    
            commit("setAllUsersCount", response.data.totalItems );    
            //totalItems      
            commit("setIsAllUsersLoading", false);
        })
        .catch((error: AxiosError<Error>) => {
          commit("setIsAllUsersLoading", false);
        });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
